import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Archive, X, RotateCcw, Trash2, Clock, Package } from 'lucide-react';
import { API } from '../App';
import { toast } from 'sonner';

const ArchiveModal = ({ open, onClose, onRestored }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retentionDays, setRetentionDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pingmes/archive`);
      setItems(res.data.pingmes || []);
      setRetentionDays(res.data.retention_days || 30);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;

  const restore = async (id) => {
    try {
      await axios.post(`${API}/pingmes/${id}/restore`);
      toast.success('PingMe restaure');
      setItems(items.filter((p) => p.pingme_id !== id));
      onRestored && onRestored();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const purge = async (id) => {
    if (!window.confirm('Supprimer definitivement ce PingMe ? Cette action est irreversible.')) return;
    try {
      await axios.delete(`${API}/pingmes/${id}/permanent`);
      toast.success('PingMe definitivement supprime');
      setItems(items.filter((p) => p.pingme_id !== id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-charcoal/50 backdrop-blur-sm px-4" onClick={onClose}>
      <div className="bg-white border-2 border-charcoal rounded-2xl shadow-brutal-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="archive-modal">
        <div className="flex items-center justify-between p-4 border-b-2 border-charcoal/10 sticky top-0 bg-white z-10">
          <h3 className="font-heading font-bold text-lg flex items-center gap-2">
            <Archive className="w-5 h-5" /> Archive ({items.length})
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-surface-alt rounded-lg" data-testid="archive-close-btn"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4">
          <p className="text-xs text-charcoal/60 mb-4">
            Les PingMes archives sont recuperables pendant <strong>{retentionDays} jours</strong>. Apres cette periode, ils sont definitivement supprimes.
          </p>

          {loading ? (
            <p className="text-center text-charcoal/60 py-8">Chargement...</p>
          ) : items.length === 0 ? (
            <div className="text-center py-12" data-testid="archive-empty">
              <Archive className="w-12 h-12 mx-auto text-charcoal/30 mb-3" />
              <p className="text-charcoal/60">Aucun PingMe archive</p>
              <p className="text-xs text-charcoal/40 mt-1">Les PingMes que vous archivez apparaitront ici.</p>
            </div>
          ) : (
            <ul className="space-y-2" data-testid="archive-list">
              {items.map((p) => (
                <li
                  key={p.pingme_id}
                  className="flex items-center gap-3 bg-white border-2 border-charcoal/10 rounded-xl p-3 hover:border-charcoal/30 transition-colors"
                  data-testid={`archive-item-${p.pingme_id}`}
                >
                  {p.photo_url ? (
                    <img
                      src={p.photo_url.startsWith('/api') ? `${API.replace('/api', '')}${p.photo_url}` : p.photo_url}
                      alt={p.name}
                      className="w-12 h-12 rounded-lg border-2 border-charcoal object-cover shrink-0"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-lg border-2 border-charcoal flex items-center justify-center bg-charcoal/5 shrink-0">
                      <Package className="w-5 h-5 text-charcoal/40" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm truncate">{p.name || 'Sans nom'}</p>
                    <p className="text-[10px] font-mono text-charcoal/50 truncate">{p.pingme_id}</p>
                    <p className="text-[11px] text-charcoal/60 flex items-center gap-1 mt-0.5">
                      <Clock className="w-3 h-3" />
                      {p.days_remaining > 0 ? `${p.days_remaining} jour${p.days_remaining > 1 ? 's' : ''} restant${p.days_remaining > 1 ? 's' : ''}` : 'Expire bientot'}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      onClick={() => restore(p.pingme_id)}
                      className="flex items-center gap-1 bg-primary text-charcoal border-2 border-charcoal rounded-full px-3 py-1.5 text-xs font-bold hover:bg-primary/80"
                      data-testid={`archive-restore-${p.pingme_id}`}
                      title="Restaurer"
                    >
                      <RotateCcw className="w-3.5 h-3.5" /> Restaurer
                    </button>
                    <button
                      onClick={() => purge(p.pingme_id)}
                      className="p-1.5 text-charcoal/50 hover:text-danger hover:bg-danger/10 rounded-lg"
                      data-testid={`archive-purge-${p.pingme_id}`}
                      title="Supprimer definitivement"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArchiveModal;
